import { useState, useMemo, useRef } from "react";
import { useAdminModels, useAdminUpdateModel } from "../hooks/useApi";
import AdminModelDetailDrawer from "../components/AdminModelDetailDrawer";
import "../admin-theme.css";

function usdPrice(val: unknown, basis: string | undefined): string | null {
  if (val == null) return null;
  const perM = (basis || "per_1m") === "per_1m";
  const n = perM ? Number(val) * 1_000_000 : Number(val);
  if (n === 0) return null;
  return n < 0.01 ? `$${n.toFixed(6)}` : `$${n.toFixed(2)}`;
}

function usdPerMillionToIrr(usd: number): number {
  return usd * 500;
}

function irrToUsdPerMillion(irr: number): number {
  if (!irr) return 0;
  return irr / 500;
}

const MODEL_STATUSES = ["active", "inactive", "deprecated"];

type SortField = "model_name" | "provider" | "input_price" | "output_price" | "context_length" | "status" | "created_at" | "last_updated" | "updated_at";
type Order = "asc" | "desc";

interface ColumnDef {
  key: string;
  label: string;
  visible: boolean;
  width?: string;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: "model_name", label: "Model Name", visible: true, width: "minmax(220px, 1fr)" },
  { key: "provider", label: "Provider", visible: true, width: "120px" },
  { key: "type", label: "Type", visible: true, width: "100px" },
  { key: "weekly_tokens", label: "Weekly Tokens", visible: false, width: "110px" },
  { key: "input_price", label: "Input USD", visible: true, width: "100px" },
  { key: "output_price", label: "Output USD", visible: true, width: "100px" },
  { key: "context_length", label: "Context", visible: true, width: "100px" },
  { key: "created", label: "Released", visible: false, width: "100px" },
  { key: "performance", label: "Performance", visible: false, width: "100px" },
  { key: "latency", label: "Latency", visible: false, width: "100px" },
  { key: "throughput", label: "Throughput", visible: false, width: "100px" },
  { key: "intelligence", label: "Intelligence", visible: false, width: "100px" },
  { key: "coding", label: "Coding", visible: false, width: "100px" },
  { key: "agentic", label: "Agentic", visible: false, width: "100px" },
  { key: "is_free", label: "Free", visible: false, width: "80px" },
  { key: "set_manually", label: "Manual", visible: true, width: "80px" },
  { key: "status", label: "Status", visible: true, width: "90px" },
  { key: "capacity_tpm", label: "TPM", visible: false, width: "90px" },
  { key: "updated_at", label: "Last Fetch", visible: true, width: "140px" },
  { key: "last_updated", label: "Last Update", visible: true, width: "140px" },
  { key: "actions", label: "", visible: true, width: "90px" },
];

const MODEL_TYPES = [
  { value: "", label: "All Types" },
  { value: "text", label: "Text" },
  { value: "multimodal", label: "Multimodal" },
  { value: "image", label: "Image" },
  { value: "audio", label: "Audio" },
  { value: "video", label: "Video" },
  { value: "embedding", label: "Embedding" },
];

const LABEL_MAP: Record<string, string> = {
  model_name: "Model Name",
  provider: "Provider",
  type: "Type",
  input_price: "Input USD",
  output_price: "Output USD",
  context_length: "Context",
  status: "Status",
  is_free: "Free",
  set_manually: "Manual",
  capacity_tpm: "TPM",
  performance: "Performance",
  latency: "Latency",
  throughput: "Throughput",
  intelligence: "Intelligence",
  coding: "Coding",
  agentic: "Agentic",
  updated_at: "Last Fetch",
  last_updated: "Last Update",
};

export default function AdminModels() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [modelType, setModelType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [manualFilter, setManualFilter] = useState("");
  const [sort, setSort] = useState<SortField>("model_name");
  const [order, setOrder] = useState<Order>("asc");
  const [columns, setColumns] = useState<ColumnDef[]>(ALL_COLUMNS);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [editModel, setEditModel] = useState<Record<string, unknown> | null>(null);
  const [editInputPrice, setEditInputPrice] = useState("");
  const [editOutputPrice, setEditOutputPrice] = useState("");
  const [editStatus, setEditStatus] = useState("active");
  const [detailModel, setDetailModel] = useState<Record<string, unknown> | null>(null);
  const overlayMouseDown = useRef(false);

  const params = useMemo(
    () => ({
      search: debouncedSearch,
      model_type: modelType,
      status_filter: statusFilter,
      set_manually_filter: manualFilter,
      sort,
      order,
      limit: 200,
    }),
    [debouncedSearch, modelType, statusFilter, manualFilter, sort, order]
  );

  const { data: response, isLoading } = useAdminModels(params);
  const models = (response as { items?: Record<string, unknown>[]; total_count?: number })?.items;
  const totalCount = (response as { items?: Record<string, unknown>[]; total_count?: number })?.total_count;
  const updateModel = useAdminUpdateModel();

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setTimeout(() => setDebouncedSearch(value), 300);
  };

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSort(field);
      setOrder("asc");
    }
  };

  const visibleColumns = columns.filter((c) => c.visible);
  const sortArrow = (field: SortField) => {
    if (sort !== field) return "";
    return order === "asc" ? " ↑" : " ↓";
  };

  const toggleColumn = (key: string) => {
    setColumns((prev) =>
      prev.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c))
    );
  };

  const toggleSetManually = (model: Record<string, unknown>) => {
    updateModel.mutate({
      id: String(model.id),
      data: { set_manually: !model.set_manually },
    });
  };

  const handleSave = () => {
    if (editModel) {
      const inputIrr = usdPerMillionToIrr(parseFloat(editInputPrice || "0"));
      const outputIrr = usdPerMillionToIrr(parseFloat(editOutputPrice || "0"));
      updateModel.mutate({
        id: String(editModel.id),
        data: {
          input_price: inputIrr,
          output_price: outputIrr,
          status: editStatus,
        },
      });
      setEditModel(null);
    }
  };

  const openEdit = (model: Record<string, unknown>) => {
    setEditModel(model);
    setEditInputPrice(String(irrToUsdPerMillion(Number(model.input_price ?? 0))));
    setEditOutputPrice(String(irrToUsdPerMillion(Number(model.output_price ?? 0))));
    setEditStatus((model.status as string) || "active");
  };

  const sortableFields: SortField[] = [
    "model_name", "provider", "input_price", "output_price",
    "context_length", "status", "created_at", "updated_at", "last_updated",
  ];

  const formatCell = (model: Record<string, unknown>, col: ColumnDef): string | number | React.ReactNode => {
    const val = model[col.key];
    const isManual = Boolean(model.set_manually);

    switch (col.key) {
      case "model_name":
        return (
          <div className="flex flex-col">
            <span className="font-mono text-xs" style={{ color: "var(--admin-text-primary)", direction: "ltr", textAlign: "left" }}>
              {model.model_name as string}
            </span>
            {(model.display_name as string) && (
              <span className="text-xs mt-0.5" style={{ color: "var(--admin-text-tertiary)" }}>
                {model.display_name as string}
              </span>
            )}
          </div>
        );
      case "type":
        return (
          <span className="admin-badge admin-badge-info" style={{ fontSize: 11 }}>
            {(model.model_type as string) || "text"}
          </span>
        );
      case "input_price": {
        const perM = usdPrice(model.pricing_prompt, model.price_basis as string | undefined);
        return perM === null ? "—" : perM;
      }
      case "output_price": {
        const perM = usdPrice(model.pricing_completion, model.price_basis as string | undefined);
        return perM === null ? "—" : perM;
      }
      case "context_length":
        if (val == null) return "—";
        return (val as number).toLocaleString("fa-IR");
      case "created":
        return model.created
          ? new Date((model.created as number) * 1000).toLocaleDateString("fa-IR")
          : "—";
      case "status":
        return statusBadge(val as string, isManual);
      case "is_free":
        return val ? (
          <span className="admin-badge admin-badge-success" style={{ fontSize: 11 }}>Free</span>
        ) : (
          <span style={{ color: "var(--admin-text-tertiary)" }}>—</span>
        );
      case "set_manually":
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSetManually(model);
            }}
            className={`admin-toggle${isManual ? " on" : ""}`}
            title={isManual ? "Switch to auto-sync" : "Switch to manual"}
          />
        );
      case "updated_at": {
        const raw = model.updated_at as string;
        if (!raw) return <span style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
        try {
          const d = new Date(raw);
          if (isNaN(d.getTime())) return "—";
          const formatted = d.toLocaleString("fa-IR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <span className="text-xs" dir="ltr" title={d.toISOString()} style={{ color: "var(--admin-text-secondary)", whiteSpace: "nowrap" }}>
              {formatted}
            </span>
          );
        } catch {
          return "—";
        }
      }
      case "last_updated": {
        const raw = model.last_updated as string;
        if (!raw) return <span style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
        try {
          const d = new Date(raw);
          if (isNaN(d.getTime())) return "—";
          const formatted = d.toLocaleString("fa-IR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <span className="text-xs" dir="ltr" title={d.toISOString()} style={{ color: "var(--admin-text-secondary)", whiteSpace: "nowrap" }}>
              {formatted}
            </span>
          );
        } catch {
          return "—";
        }
      }
      case "capacity_tpm":
        return val != null ? (val as number).toLocaleString("fa-IR") : "—";
      case "provider":
        return (
          <span className="text-xs" style={{ direction: "ltr", textAlign: "left", display: "block" }}>
            {val as string}
          </span>
        );
      case "weekly_tokens":
      case "performance":
      case "latency":
      case "throughput":
      case "intelligence":
      case "coding":
      case "agentic":
        return "—";
      case "actions":
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEdit(model);
            }}
            disabled={!isManual}
            className="admin-btn admin-btn-secondary admin-btn-sm"
            style={{ opacity: isManual ? 1 : 0.4, cursor: isManual ? "pointer" : "not-allowed" }}
            title={isManual ? "Edit pricing & status" : "Toggle Manual first to edit"}
          >
            Edit
          </button>
        );
      default:
        return val != null ? String(val) : "—";
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)", fontFamily: "'Vazirmatn', 'Inter', system-ui, sans-serif" }}>مدل‌ها</h1>
        <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)", fontFamily: "'Vazirmatn', 'Inter', system-ui, sans-serif" }}>
          مرور و مدیریت همه مدل‌های هوش مصنوعی دریافت‌شده از OpenRouter
        </p>
      </div>

      {/* Toolbar */}
      <div className="admin-card">
        <div className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2"
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="var(--admin-text-tertiary)" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search models..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="admin-input"
              style={{ paddingRight: 40 }}
            />
          </div>

          <select
            value={modelType}
            onChange={(e) => setModelType(e.target.value)}
            className="admin-input"
            style={{ width: 140 }}
          >
            {MODEL_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-input"
            style={{ width: 140 }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deprecated">Deprecated</option>
          </select>

          <select
            value={manualFilter}
            onChange={(e) => setManualFilter(e.target.value)}
            className="admin-input"
            style={{ width: 150 }}
          >
            <option value="">All Sync Modes</option>
            <option value="true">Manual</option>
            <option value="false">Auto-synced</option>
          </select>

          {(statusFilter || manualFilter || modelType || debouncedSearch) && (
            <button
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setModelType("");
                setStatusFilter("");
                setManualFilter("");
              }}
              className="admin-btn admin-btn-secondary admin-btn-sm"
              title="Reset all filters"
            >
              Reset
            </button>
          )}

          {/* Column Selector */}
          <div className="relative">
            <button
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              className="admin-btn admin-btn-secondary admin-btn-sm"
              title="Select columns"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
              Columns
            </button>

            {showColumnPicker && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowColumnPicker(false)} />
                <div
                  className="admin-glass absolute left-0 top-full mt-2 z-50 rounded-2xl p-3 shadow-lg"
                  style={{ minWidth: 220, border: "1px solid var(--admin-border)" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="text-xs font-semibold mb-2 px-1" style={{ color: "var(--admin-text-secondary)" }}>
                    Show / Hide Columns
                  </p>
                  {columns.map((col) =>
                    col.key !== "actions" ? (
                      <label
                        key={col.key}
                        className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors hover"
                        style={{ color: "var(--admin-text-primary)", fontSize: 13 }}
                      >
                        <input
                          type="checkbox"
                          checked={col.visible}
                          onChange={() => toggleColumn(col.key)}
                          style={{ accentColor: "var(--admin-primary)" }}
                        />
                        {LABEL_MAP[col.key] || col.label}
                      </label>
                    ) : null
                  )}
                </div>
              </>
            )}
          </div>

          <span className="text-xs mr-auto" style={{ color: "var(--admin-text-tertiary)" }}>
            {totalCount != null ? `${totalCount.toLocaleString("fa-IR")} مدل` : ""}
          </span>
        </div>
      </div>

      {/* Models Table */}
      <div className="admin-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !models?.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-50" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 12h6M12 9v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <p className="text-sm">No models found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table" style={{ tableLayout: "auto" }}>
              <thead>
                <tr>
                  {visibleColumns.map((col) =>
                    col.key === "actions" ? (
                      <th key={col.key} style={{ width: col.width, textAlign: "center" }}></th>
                    ) : sortableFields.includes(col.key as SortField) ? (
                      <th
                        key={col.key}
                        style={{ width: col.width, cursor: "pointer" }}
                        onClick={() => handleSort(col.key as SortField)}
                      >
                        {LABEL_MAP[col.key] || col.label}{sortArrow(col.key as SortField)}
                      </th>
                    ) : (
                      <th key={col.key} style={{ width: col.width }}>
                        {LABEL_MAP[col.key] || col.label}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {(models as Record<string, unknown>[]).map((model) => (
                  <tr
                    key={model.id as string}
                    onClick={() => setDetailModel(model)}
                    style={{ cursor: "pointer" }}
                    title="View model details"
                  >
                    {visibleColumns.map((col) => (
                      <td key={col.key} style={
                        col.key === "model_name" || col.key === "provider"
                          ? { direction: "ltr", textAlign: "left" }
                          : col.key === "set_manually"
                          ? { textAlign: "center" }
                          : undefined
                      }>
                        {formatCell(model, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModel && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setEditModel(null); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                Edit: {editModel.model_name as string}
              </h2>
              <button
                onClick={() => setEditModel(null)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  Status
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="admin-input"
                >
                  {MODEL_STATUSES.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Input Price */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  Input Price (USD per 1M tokens)
                </label>
                <input
                  type="text"
                  value={editInputPrice}
                  onChange={(e) => setEditInputPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="admin-input"
                  dir="ltr"
                />
              </div>

              {/* Output Price */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  Output Price (USD per 1M tokens)
                </label>
                <input
                  type="text"
                  value={editOutputPrice}
                  onChange={(e) => setEditOutputPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="admin-input"
                  dir="ltr"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setEditModel(null)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateModel.isPending}
                  className="admin-btn admin-btn-primary flex-1"
                >
                  {updateModel.isPending ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Model Detail Drawer */}
      <AdminModelDetailDrawer selectedModel={detailModel} onClose={() => setDetailModel(null)} />
    </div>
  );
}

function statusBadge(status: string, isManual?: boolean) {
  const style: React.CSSProperties = { fontSize: 11 };
  if (isManual) {
    style.border = "1px dashed var(--admin-primary)";
  }
  switch (status) {
    case "active":
      return <span className="admin-badge admin-badge-success" style={style}>Active</span>;
    case "inactive":
      return <span className="admin-badge admin-badge-warning" style={style}>Inactive</span>;
    case "deprecated":
      return <span className="admin-badge admin-badge-error" style={style}>Deprecated</span>;
    default:
      return <span className="admin-badge admin-badge-info" style={style}>{status}</span>;
  }
}
